import { ObjectShare } from '@reporting/rp-types/core';
import { msg } from "@omegagrid/localize";
import { FormAdapter, FormContainerOptions, FormControlType, FormValues, FormControlProperties, Form } from '@omegagrid/form';
import type { TreeSource } from '@omegagrid/tree';
import type { Service } from "../client/service";
import rp from "../global";
import { SyncTypes } from "../editors/syncEditorAdapter";
import { combineSyncObjects, listKeys } from './syncFormValues';

export type SyncFormField = {
	key: 'accounting_share'|'id_object'|'id_share_object'|'only_share',
	/** the choices of the list - a flat list or a tree (nodes with levels, headings with `ch: false`); the keys as strings */
	getSourceData?: (params?: any) => Promise<TreeSource>
};

/**
 * The form that registers an object of the module for synchronization or sharing with a target
 * company, or edits such a registration. A new registration chooses several objects and several
 * target companies at once (multiple-choice lists) and registers every combination; the pairs that
 * exist already are left out and an alert says how many. An edit keeps to its one object and company.
 */
export class SyncFormAdapter extends FormAdapter {

	private object: ObjectShare;
	private accountingShare: number;

	constructor(
        private syncClient: Service,
		private formFields: SyncFormField[],
		private objectId?: number,
		private msEndpoint: string = 'sync/objects'
	) { super(); }

	/** A new registration chooses several objects and companies, an edit keeps to one */
	private get multiple() { return !this.objectId; }

	getColumns() {
		return [
			{ width: 150 },
			{ width: undefined },
		];
	}

	async loadObjectShare() {
		if (this.objectId && !this.object) {
			this.object = await this.syncClient.get<ObjectShare>(`${this.msEndpoint}/${this.objectId}`);
			this.accountingShare = this.object?.accounting_share || null;
		}
	}

	private listOptions() {
		return this.multiple
			? { selectMode: 'multiple', search: true }
			: { selectMode: 'single', autoClose: true, search: true };
	}

	/**
	 * The companies come as a tree by groups: the level badges of a group pick its companies, "All"
	 * picks the companies a typed filter matches. No badges over the whole tree - its first level
	 * holds only the group headings, which are never picked.
	 */
	private companyListOptions() {
		return this.multiple
			? { ...this.listOptions(), globalLevels: false, selectAllMode: 'filtered' }
			: this.listOptions();
	}

	async getControls(): Promise<FormControlProperties[]> {
		await this.loadObjectShare();
		const controls: FormControlProperties[] = [];
		let r = 0;
		for (const f of this.formFields) {
			if (f.key === 'id_object') {
				controls.push(
					{ row: r, col: 0, type: FormControlType.Label, value: msg('Object'), for: 'id_object' },
					{ row: r++, col: 1, type: FormControlType.List, key: 'id_object', value: this.multiple ? null : (this.object?.id_object || ''), required: true , content: {
						sourceData: await f.getSourceData(),
						options: this.listOptions()
					}}
				);
			}
			if (f.key === 'accounting_share') {
				controls.push(
					{ row: r, col: 0, type: FormControlType.Label, value: msg('Target company'), for: 'accounting_share' },
					// the value as the key of its node: the keys of the tree are strings
					{ row: r++, col: 1, type: FormControlType.List, key: 'accounting_share', value: this.accountingShare == null ? null : String(this.accountingShare), required: true, content: {
						sourceData: await f.getSourceData(),
						options: this.companyListOptions()
					}}
				);
			}
			if (f.key === 'id_share_object') {
				controls.push(
					{ row: r, col: 0, type: FormControlType.Label, value: msg('Target object'), for: 'id_share_object' },
					{ row: r++, col: 1, type: FormControlType.List, key: 'id_share_object', value: this.object?.id_share_object || '', content: {
						sourceData: await f.getSourceData(this.accountingShare),
						options: { selectMode: 'single', autoClose: true, search: true }
					}}
				);
			}
			if (f.key === 'only_share') {
				// the flag as a choice of the type: synchronization copies the object, sharing shows it where it is
				controls.push(
					{ row: r, col: 0, type: FormControlType.Label, value: msg('Type'), for: 'type' },
					{ row: r++, col: 1, type: FormControlType.List, key: 'type', value: this.object?.only_share ? SyncTypes.SHARE : SyncTypes.SYNC, required: true, content: {
						sourceData: [
							{ k: SyncTypes.SYNC, v: msg('Synchronization') },
							{ k: SyncTypes.SHARE, v: msg('Sharing') },
						],
						options: { selectMode: 'single', autoClose: true }
					}}
				);
			}
		}
		return controls;
	}

	getOptions(): FormContainerOptions {
		return {
			buttonsPosition: 'bottom',
			alertsPosition: 'bottom',
			buttons: ["save", "close"]
		};
	}

	async save(values: Map<string|number, FormValues>): Promise<boolean> {
		const formValues = values.get(0);
		const has = (key: SyncFormField['key']) => this.formFields.some(f => f.key === key);
		// the type list stands for the only_share flag of the registration
		const onlyShare = has('only_share') ? Number(listKeys(formValues.type)[0]) === SyncTypes.SHARE : undefined;

		if (this.object) {
			const data: ObjectShare = { idz: this.object.idz };
			for (const f of this.formFields) {
				const [value] = listKeys(formValues[f.key]); // an empty choice (the target object "<New>") stays out
				if (value === undefined) continue;
				if (f.key === 'accounting_share') data.accounting_share = Number(value);
				else if (f.key === 'id_object') data.id_object = String(value);
				else if (f.key === 'id_share_object') data.id_share_object = String(value);
			}
			if (onlyShare !== undefined) data.only_share = onlyShare;
			await this.syncClient.post(this.msEndpoint, data);
			return true;
		}

		const objects = has('id_object') ? listKeys(formValues.id_object) : null;
		const accountings = listKeys(formValues.accounting_share);
		// the lists validate nothing themselves; a thrown error is what keeps the form open with the message
		if (objects && !objects.length) throw new Error(msg('Select an object'));
		if (!accountings.length) throw new Error(msg('Select a target company'));

		const common: ObjectShare = {};
		if (onlyShare !== undefined) common.only_share = onlyShare;
		const [shareObject] = has('id_share_object') ? listKeys(formValues.id_share_object) : [];
		if (shareObject !== undefined) common.id_share_object = String(shareObject);

		const rows = combineSyncObjects(objects, accountings, common);
		if (rows.length === 1) {
			await this.syncClient.post(this.msEndpoint, rows[0]); // a pair that exists already is refused (409)
			return true;
		}
		const saved = await this.syncClient.post<ObjectShare[]>(this.msEndpoint, rows);
		const skipped = rows.length - saved.length;
		if (skipped > 0) {
			rp.host.showAlert({ type: 'info', content: `${msg('Added')}: ${saved.length}, ${msg('already existing')}: ${skipped}`, title: msg('Synchronization'), timeout: 6000 });
		}
		return true;
	}

	// use to update form controls based on accounting share change, e.g. to reload id_share_object list
	// event listener example:
	// component.addEventListener('change', (e: ControlEvent<Form>) => {
		// if (e.model.props.key === 'accounting_share') (component.adapter as SyncFormAdapter).updateFormControls(component.forms[0]);
	// });
	async updateFormControls(form: Form) {
		this.component.showOverlay();
		// the target object is offered for one chosen company; with several (or none) only a new one is possible
		const accountings = listKeys(form.values.accounting_share);
		this.accountingShare = accountings.length === 1 ? Number(accountings[0]) : null;
		form.values.id_share_object = '';
		form.setSourceData({
			controls: this.updateValues(await this.getControls(), form.values),
			columns: this.getColumns()
		});
		this.component.hideOverlay();
	}

	updateValues = (controls: FormControlProperties[], values: FormValues) => {
		if (!values) return controls;
		const map = new Map(controls.filter(item => item.key).map(item => [item.key, item]));
		Object.entries(values).forEach(([k, v]) => Object.assign(map.get(k) || {}, { value: v }));
		return controls;
	};
}
